import {
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  ReturnDocumentCandidate,
  ReturnEligibility,
  ReturnPreview,
  SpringPage,
} from '../../../types/fiscal/ReturnEvent';

import { FiscalEventSubmission } from '../../../types/fiscal/FiscalEvent';

// =====================================================
// 📦 Tipos
// =====================================================

export interface SelectedReturnDocument {
  candidate?: ReturnDocumentCandidate;
  eligibility: ReturnEligibility;
}

interface ReturnEventState {
  // =====================================================
  // 🔍 Buscador
  // =====================================================

  documentResults: ReturnDocumentCandidate[];
  documentPaginator:
    | SpringPage<ReturnDocumentCandidate>
    | null;

  searchingDocuments: boolean;

  // =====================================================
  // 📄 Documentos seleccionados
  // =====================================================

  documents: SelectedReturnDocument[];

  // =====================================================
  // 📦 Líneas
  // =====================================================

  selectedLines: Record<string, boolean>;

  quantities: Record<string, string>;

  unitPrices: Record<string, string>;

  discounts: Record<string, string>;

  // =====================================================
  // 🧮 Preview
  // =====================================================

  preview: ReturnPreview | null;

  previewLoading: boolean;

  // =====================================================
  // ✅ Resultado
  // =====================================================

  result: FiscalEventSubmission | null;

  // =====================================================
  // ⏳ Estado UI
  // =====================================================

  busy: boolean;

  error: string | null;
}

// =====================================================
// 🏁 Estado inicial
// =====================================================

const initialState: ReturnEventState = {
  documentResults: [],
  documentPaginator: null,

  searchingDocuments: false,

  documents: [],

  selectedLines: {},
  quantities: {},
  unitPrices: {},
  discounts: {},

  preview: null,
  previewLoading: false,
  result: null,

  busy: false,

  error: null,
};

// =====================================================
// 🧩 Slice
// =====================================================

const returnEventSlice = createSlice({
  name: 'returnEvent',

  initialState,

  reducers: {
    // =====================================================
    // 🔍 BUSCADOR
    // =====================================================

    setReturnDocumentSearch(
      state,
      action: PayloadAction<
        SpringPage<ReturnDocumentCandidate>
      >
    ) {
      state.documentResults =
        action.payload.content ?? [];

      state.documentPaginator =
        action.payload;
    },

    clearReturnDocumentSearch(state) {
      state.documentResults = [];
      state.documentPaginator = null;
    },

    setSearchingReturnDocuments(
      state,
      action: PayloadAction<boolean>
    ) {
      state.searchingDocuments =
        action.payload;
    },

    // =====================================================
    // 📄 DOCUMENTOS
    // =====================================================

    addReturnDocument(
      state,
      action: PayloadAction<SelectedReturnDocument>
    ) {
      const exists =
        state.documents.some(
          (document) =>
            document.eligibility.saleId ===
            action.payload.eligibility.saleId
        );

      if (!exists) {
        state.documents.push(
          action.payload
        );
      }
    },

    removeReturnDocument(
      state,
      action: PayloadAction<number>
    ) {
      const saleId = action.payload;

      state.documents =
        state.documents.filter(
          (document) =>
            document.eligibility.saleId !== saleId
        );

      state.selectedLines =
        Object.fromEntries(
          Object.entries(
            state.selectedLines
          ).filter(
            ([key]) =>
              !key.startsWith(
                `${saleId}:`
              )
          )
        );

      state.quantities =
        Object.fromEntries(
          Object.entries(
            state.quantities
          ).filter(
            ([key]) =>
              !key.startsWith(
                `${saleId}:`
              )
          )
        );

      state.unitPrices =
        Object.fromEntries(
          Object.entries(
            state.unitPrices
          ).filter(
            ([key]) =>
              !key.startsWith(
                `${saleId}:`
              )
          )
        );

      state.discounts =
        Object.fromEntries(
          Object.entries(
            state.discounts
          ).filter(
            ([key]) =>
              !key.startsWith(
                `${saleId}:`
              )
          )
        );

      state.preview = null;
    },

    // =====================================================
    // ☑️ SELECCIÓN
    // =====================================================

    setReturnLineSelected(
      state,
      action: PayloadAction<{
        key: string;
        selected: boolean;
        quantity?: string;
        unitPrice?: string;
        discount?: string;
      }>
    ) {
      const {
        key,
        selected,
        quantity,
        unitPrice,
        discount,
      } = action.payload;

      state.selectedLines[key] =
        selected;

      state.quantities[key] =
        selected
          ? quantity ?? state.quantities[key] ?? ''
          : '';

      state.unitPrices[key] =
        selected
          ? unitPrice ?? state.unitPrices[key] ?? ''
          : '';

      state.discounts[key] =
        selected
          ? discount ?? state.discounts[key] ?? ''
          : '';

      state.preview = null;
    },

    setReturnLineQuantity(
      state,
      action: PayloadAction<{
        key: string;
        quantity: string;
      }>
    ) {
      state.quantities[
        action.payload.key
      ] =
        action.payload.quantity;

      state.preview = null;
    },

    setReturnLineUnitPrice(
      state,
      action: PayloadAction<{
        key: string;
        unitPrice: string;
      }>
    ) {
      state.unitPrices[
        action.payload.key
      ] =
        action.payload.unitPrice;

      state.preview = null;
    },

    setReturnLineDiscount(
      state,
      action: PayloadAction<{
        key: string;
        discount: string;
      }>
    ) {
      state.discounts[
        action.payload.key
      ] =
        action.payload.discount;

      state.preview = null;
    },

    setReturnDocumentLines(
      state,
      action: PayloadAction<{
        saleId: number;
        lines: Array<{
          productId: number;
          availableQuantity: number;
          unitPrice: number;
          discount: number;
        }>;
        selected: boolean;
      }>
    ) {
      const {
        saleId,
        lines,
        selected,
      } = action.payload;

      lines.forEach((line) => {
        const key =
          `${saleId}:${line.productId}`;

        state.selectedLines[key] =
          selected;

        state.quantities[key] =
          selected
            ? String(
                line.availableQuantity
              )
            : '';

        state.unitPrices[key] =
          selected
            ? String(
                line.unitPrice
              )
            : '';

        state.discounts[key] =
          selected
            ? String(
                line.discount
              )
            : '';
      });

      state.preview = null;
    },

    // =====================================================
    // 🧮 PREVIEW
    // =====================================================

    setReturnPreview(
      state,
      action: PayloadAction<
        ReturnPreview | null
      >
    ) {
      state.preview = action.payload;
    },

    clearReturnPreview(state) {
      state.preview = null;
    },

    setReturnPreviewLoading(
      state,
      action: PayloadAction<boolean>
    ) {
      state.previewLoading =
        action.payload;
    },

    // =====================================================
    // ✅ RESULTADO
    // =====================================================

    setReturnResult(
      state,
      action: PayloadAction<
        FiscalEventSubmission | null
      >
    ) {
      state.result =
        action.payload;
    },

    // =====================================================
    // ⏳ UI
    // =====================================================

    setReturnBusy(
      state,
      action: PayloadAction<boolean>
    ) {
      state.busy =
        action.payload;
    },

    setReturnError(
      state,
      action: PayloadAction<
        string | null
      >
    ) {
      state.error =
        action.payload;
    },

    // =====================================================
    // 🧹 RESET
    // =====================================================

    resetReturnEvent(state) {
      state.documentResults = [];
      state.documentPaginator = null;

      state.searchingDocuments = false;

      state.documents = [];

      state.selectedLines = {};
      state.quantities = {};
      state.unitPrices = {};
      state.discounts = {};

      state.preview = null;
      state.previewLoading = false;
      state.result = null;

      state.busy = false;
      state.error = null;
    },
  },
});

// =====================================================
// 📤 Actions
// =====================================================

export const {
  setReturnDocumentSearch,
  clearReturnDocumentSearch,
  setSearchingReturnDocuments,

  addReturnDocument,
  removeReturnDocument,

  setReturnLineSelected,
  setReturnLineQuantity,
  setReturnLineUnitPrice,
  setReturnLineDiscount,
  setReturnDocumentLines,

  setReturnPreview,
  clearReturnPreview,
  setReturnPreviewLoading,

  setReturnResult,

  setReturnBusy,
  setReturnError,

  resetReturnEvent,
} = returnEventSlice.actions;

// =====================================================
// 📤 Reducer
// =====================================================

export default returnEventSlice.reducer;